import React, { useRef } from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import i18next from 'i18next';
import { Dataset } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
//import { formatAxisAmmountValue } from 'helpers/chart';
import { isoDateTimeToLabel } from 'helpers/helpers';

import { Currency, PersonType, EdwardColors } from 'enums';
import { AccountEditPlan } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';

import { assembleEvents } from 'pages/Accounts/Edit/Plan/Chart/helpers';
import { computeChartData } from 'helpers/chart';
import { defaultChartData } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { ExternalProductData } from 'pages/Accounts/Edit/components/ExternalProducts/model';

import { offsetLabel } from 'pages/Accounts/Edit/Plan/Chart/helpers';
import colors from 'components/PortfolioTables/colors';
import { grayscale } from 'helpers/chart';

interface Colors {
  [key: string]: string;
}
interface ColorOpacity {
  [key: string]: number;
}
//const CHART_AREA_HEIGHT = 162;
const CHART_SPACING_TOP = 80;

interface Props {
  planData: AccountEditPlan;
  rentApplied: boolean;
  original?: boolean;
  title?: string;
  className?: string;
  currency: Currency;
  labelType?: LabelType;
  portfolioOnly?: boolean;
  showRange?: boolean;
  xAxisTitle?: string;
  personType: PersonType;
  basicColors: Colors;
  colorOpacity: ColorOpacity;
}

export enum LabelType {
  DATE = 'date',
  DATETIME = 'datetime',
}

export interface ChartData {
  name: string;
  type: 'area' | 'line';
  color?: string;
  data: string[][];
  detailData?: string[][];
  fillOpacity: number;
  visible: boolean;
  showInLegend: boolean;
  id?: string;
}

const chart = (
  data: any,
  basicColors: Colors,
  colorOpacity: ColorOpacity,
  portfolioOnly?: boolean,
  altColors?: boolean,
  extProducts?: ExternalProductData[],
): ChartData[] | [] => {
  const datasets = data;
  const datasetsToIgnore = ['total.title', 'client.age', 'targets', 'deposits'];
  return datasets
    .filter((single: Dataset) => datasetsToIgnore.indexOf(single.title) === -1)
    .map((single: Dataset): ChartData => {
      const extProd = extProducts && single.title.substr(0, 15) === 'externalProduct';
      const property = extProducts && single.title.substr(0, 8) === 'property';
      const loan = extProducts && single.title.substr(0, 4) === 'loan';

      const extProdId = extProd || property || loan ? single.title.substr(extProd ? 16 : property ? 9 : 5) : undefined;

      const extProdData = extProdId ? extProducts!.find((prod) => prod.id === extProdId) : undefined;
      const extProdInst = extProdData ? extProdData.financialInstitution?.title : undefined;
      const extProdTitle = extProdData ? extProdData.title : undefined;
      const extProdName =
        extProdInst && extProdTitle
          ? extProdInst + `<span>${extProdTitle.length > 15 ? extProdTitle.substr(0, 15) + '…' : extProdTitle}</span>`
          : extProdTitle
          ? extProdTitle
          : undefined;
      const extProdColor = extProd
        ? extProdData?.financialInstitution?.colour
        : property
        ? colors[`PROPERTY_TYPE_${extProdData?.type}`]
        : loan
        ? EdwardColors.GOLD
        : undefined;

      return {
        name: extProdName || i18next.t(`${single.title}${data?.portfolioOnly ? '_portfolioOnly' : ''}`),
        data: loan ? [] : (Object.entries(single.data) as []),
        detailData: loan ? [] : (Object.entries(single.data || single.detailData) as []),
        type: 'area',
        color: extProdColor
          ? altColors
            ? grayscale(extProdColor)
            : extProdColor
          : basicColors[`${single.title.split('.')[1].toUpperCase()}${altColors ? '_ALT' : ''}`],
        fillOpacity: extProdData ? 0.85 : colorOpacity[`${single.title.split('.')[1].toUpperCase()}_OPACITY`],
        visible: extProdData ? !!extProdData.includedInChart : true,
        showInLegend: extProdData ? !!extProdData.includedInChart : true,
        id: extProdId,
      };
    });
  //.filter((ds: ChartData) => ds.visible);
};

const total = (data: any[]): any[] => {
  const set = data.find((set: Dataset) => set.title === 'total.title')!.data;
  return Object.keys(set).map((d: string) => [d, set[d]]);
};

const PlanChart: React.FC<Props> = ({
  planData,
  className,
  title,
  currency,
  labelType = LabelType.DATE,
  portfolioOnly,
  showRange,
  xAxisTitle,
  personType,
  basicColors,
  colorOpacity,
  original,
}) => {
  const chartDataset = chart(
    planData.chart.dataset,
    basicColors,
    colorOpacity,
    portfolioOnly,
    original,
    planData.externalProductData?.externalProducts,
  );
  const agesDataset = planData.chart.dataset!.find((ds) => ds.title === 'client.age');
  const totalIndex = planData.chart.dataset!.findIndex((set) => set.title === 'total.title');
  const dates = Object.keys(planData.chart.dataset![totalIndex].data).map((d) => d);
  const indexOfDate = portfolioOnly || showRange ? dates.indexOf(planData.chart.endDate) : undefined;
  const chartRef = useRef<Highcharts.Chart | null>();

  const callback = (chart: Highcharts.Chart) => {
    chartRef.current = chart;
  };

  //FIXME: move to helper
  const xAxisLabelFormatter: Highcharts.FormatterCallbackFunction<Highcharts.AxisLabelsFormatterContextObject<number>> =
    function () {
      if (!agesDataset) {
        return personType === PersonType.LEGAL ? isoDateTimeToLabel(this.value.toString(), 'LL/yyyy') : `${this.value}`;
      }
      return (
        (agesDataset.detailData && (agesDataset.detailData[this.value] as string)) ||
        agesDataset.data[this.value] + ` ${i18next.t('general_years')}`
      ); // need to return a string
    };
  //FIXME: move to helper
  const yAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
    return this.value.toLocaleString('cs', {
      currency: 'CZK',
      style: 'currency',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const setupLabels = (xMax: number, yMax: number) => {
    const { current: chartR } = chartRef;
    const dom = document.getElementById(original ? 'PlanChart-original' : 'PlanChart');
    const labels = dom ? Array.from(dom.getElementsByClassName('highcharts-plot-line-label')) : [];
    const plotLines = dom ? Array.from(dom.getElementsByClassName('custom-plot-line')) : [];
    const area = dom ? dom.getElementsByClassName('highcharts-plot-border')[0] : null;
    const chartAreaWidth = area ? Number(area.getAttribute('width')) : 1;
    const tickPx = chartAreaWidth / (xMax || 1);
    const chartStartX = area ? Number(area.getAttribute('x')) : 0;
    const chartStartY = area ? Number(area.getAttribute('y')) : 0;
    const chartAreaHeight = chartR?.plotHeight || 1;
    const series = chartR?.series as (Highcharts.Series & { yData: number[] })[];
    const visibleSeries = series ? series.filter((s) => s.visible === true) : null;
    const totals = series ? Array(series[0].yData.length).fill(0) : [];
    visibleSeries && visibleSeries.forEach((s) => s.yData.forEach((y, i) => (totals[i] += y)));

    let prevSize: number[] = [],
      prevShift: number[] = [],
      prevPos: number[] = [];

    for (let i = 0; i < labels.length; i++) {
      const id = labels[i].children[0].getAttribute('data-event-id');
      const seriesIndex = chartDataset.findIndex((ds) => ds.id && ds.id === id);

      offsetLabel(
        labels[i],
        plotLines[i],
        seriesIndex < 0 || series[seriesIndex]?.visible === true, //
        0,
        yMax,
        chartAreaHeight,
        //CHART_AREA_HEIGHT,
        CHART_SPACING_TOP,
        chartStartX,
        chartStartY,
        totals,
        tickPx,
        prevSize,
        prevShift,
        prevPos,
      );
    }
  };

  const totals = total(planData.chart.dataset!);

  const metaData = defaultChartData;
  computeChartData(planData, metaData);

  const xPlotLines = assembleEvents(planData, metaData, dates, totals, currency, '', false, true, original, true);

  // TODO: reposition tooltip in setupLabels (align prop is not precise)
  // TODO: overflowTooltip pos
  // TODO: plotLine y pos, MOD by 4 === 0 ???
  // TODO: NICE2HAVE: pack/unpack (on hover) labels placed in the same spot

  const settings: Highcharts.Options = {
    title: {
      text: title || '',
    },
    credits: {
      enabled: false,
    },
    chart: {
      width: 716,
      height: 300,
      spacingLeft: 0,
      spacingRight: 25,
      //marginLeft: 150,
      //marginRight: 10,
      //spacing: [0,10,0,0],
      spacingTop: CHART_SPACING_TOP,
      //marginBottom: 0,
      //spacingBottom: CHART_SPACING_TOP,
      animation: false,
      events: {
        render: function (this) {
          setupLabels(this.xAxis[0]!.max!, this.yAxis[0]!.max!);
        },
      },
    },

    plotOptions: {
      series: {
        animation: false,
        enableMouseTracking: false,
        //showCheckbox: true
      },
      area: {
        opacity: 1,
        fillOpacity: 1,
        stacking: 'normal',
        marker: {
          enabled: false,
        },
      },
    },
    xAxis: {
      tickInterval: planData.chart.metadata.ageAxisStep,
      labels: {
        enabled: true,
        formatter: xAxisLabelFormatter,
        x: 10,
      },
      plotLines: xPlotLines,
      type: 'category', // making xLabels dates
      max: indexOfDate,
      title: {
        text: xAxisTitle,
        align: 'high',
      },
    },
    yAxis: {
      offset: 10,
      labels: {
        align: 'right',
        x: -20,
        formatter: yAxisLabelFormatter,
      },
      title: {
        text: '',
      },
      //opposite: true,
    },
    legend: {
      align: 'left',
      layout: 'horizontal',
      verticalAlign: 'bottom',
      floating: false,
      itemDistance: 10,
      //x: -10,
      //y: -30,
      margin: -20,
      symbolPadding: 3,
      itemStyle: {
        fontSize: '0.8em',
      },
      width: '89%',
    },
    series: chartDataset,
  };

  return (
    <div id={original ? 'PlanChart-original' : 'PlanChart'}>
      <HighchartsReact highcharts={Highcharts} options={settings} className={className} callback={callback} />
    </div>
  );
};

export default PlanChart;
